import React, { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchRecentNotifications,
  fetchUnreadNotifications,
  markNotificationAsRead,
} from '../../apis/NotificationsApi';
import './NotificationDropdown.css';
import utils from '../../Utils';
import InfiniteScroll from 'react-infinite-scroll-component';

export default function NotificationDropdown({ userId, onClose, onUpdate }) {
  const dropdownRef = useRef(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Use infinite query to handle pagination
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications', userId, showUnreadOnly],
    queryFn: ({ pageParam = 1 }) =>
      showUnreadOnly
        ? fetchUnreadNotifications(userId, pageParam)
        : fetchRecentNotifications(userId, pageParam),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
  });

  const notifications = data
    ? data.pages.flatMap((page) => page.data)
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleClick = async (notif) => {
    await markNotificationAsRead(notif.id, userId);
    onUpdate();
    refetch();
    if (notif.link) window.location.href = notif.link;
  };

  const handleMarkAllRead = async () => {
    await Promise.all(
      notifications.filter((n) => !n.read_at).map((n) => markNotificationAsRead(n.id, userId))
    );
    onUpdate();
    refetch();
  };

  return (
    <div ref={dropdownRef} className="notifications-dropdown">
      <div className="dropdown-header">
        <div className="mb-3">Notifications</div>
        <div className="header-actions">
          {notifications.length > 0 && (
            <button className="mark-all" onClick={handleMarkAllRead}>
              Mark all as read
            </button>
          )}
          <button
            className={`filter-btn ${showUnreadOnly ? 'active' : ''}`}
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
          >
            {showUnreadOnly ? 'Show All' : 'Unread Only'}
          </button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="empty-state">
          {showUnreadOnly ? 'No unread notifications' : 'No notifications'}
        </div>
      ) : (
        <InfiniteScroll
          dataLength={notifications.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={<div className="loading">Loading more...</div>}
          height={320}
          scrollThreshold={0.8}
        >
          <ul className="notifications-list">
            {notifications.map((n) => (
              <li
                key={n.id}
                onClick={() => handleClick(n)}
                className={`notification-item ${n.read_at ? '' : 'unread'}`}
              >
                <img
                  src={
                    n.avatar_url ||
                    `/img/landing/girlPower/brand/Imago_principal_perfil_redes_sociales.png`
                  }
                  alt="avatar"
                  className="notif-avatar"
                />
                <div className="notif-content">
                  <div className="notif-message">{n.message}</div>
                  <div className="notif-time">
                    {utils.formatTimeAgo(n.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {isFetchingNextPage && (
            <div className="loading">Loading more...</div>
          )}
        </InfiniteScroll>
      )}
    </div>
  );
}
